/**
 * Constantes del canal de llamadas (marcador progresivo sobre Telnyx).
 *
 * Diseño en `docs/llamadas-ar-plan.md`.
 */

/**
 * Veredictos del AMD (answering machine detection) de Telnyx.
 *
 * Se guardan **tal cual llegan**, sin traducir ni agrupar. `premium` distingue
 * `human_residence` de `human_business`, y esa diferencia importa: un
 * `human_business` significa que hemos dado con una centralita, no con el deudor,
 * y no debería contar como contacto útil. Colapsarlos a "humano" perdería
 * información que después no se puede recuperar.
 *
 * `unknown` es nuestro, no de Telnyx: es el valor de una marcación que terminó
 * antes de que el AMD llegara a decidir.
 */
const AMD_VERDICTS = [
  'human_residence',
  'human_business',
  'machine',
  'silence',
  'fax_detected',
  'not_sure',
  'unknown',
];

/**
 * Resultado que tipifica el gestor al terminar una conversación.
 *
 * Son POCOS a propósito. El presupuesto de la jornada es de ~117 s por
 * descolgada, tipificación incluida: 30 s escribiendo × 84 conversaciones son 42
 * minutos, o sea 14 conversaciones perdidas. Un clic, nunca texto libre.
 *
 * 🔴 **La tipificación describe lo que PASÓ, no lo que viene después.** Hasta el
 * 22/10/2026 hacía las dos cosas: `call_back` era la única opción que no cerraba
 * el caso, así que el gestor no elegía lo ocurrido sino lo que no daba miedo.
 * Medido sobre producción: **159 de 173 tipificaciones eran `call_back`**, el
 * 91,9 %, y `payment_plan` no se usó ni una vez. El dato no servía para nada.
 *
 * Lo que viene después lo decide ahora el gestor con el control de tareas de la
 * pantalla: «llámame esta tarde» es una tipificación de lo que pasó **más** una
 * tarea con su hora. Ver `docs/llamadas-tipificacion-plan.md`.
 */
const CALL_OUTCOMES_SELECTABLE = [
  'hangs_up_on_intro',
  'interested',
  // 🔴 **`refuses` y `dispute` NO son lo mismo, y juntarlos pierde el dato que
  // decide qué se hace después.**
  //
  //   - `dispute` — discute la deuda: que no la debe, que ya pagó, que el
  //     importe está mal. **Hay algo que resolver**, y normalmente hay que
  //     pedirle documentación al cliente.
  //   - `refuses` — reconoce la deuda y se niega a pagarla. **No hay nada que
  //     resolver**: es una decisión suya, y lo que toca es apretar o escalar.
  //
  // `refuses` existía en la lista vieja como «Se niega» y se retiró el
  // 22/10/2026 mapeándolo a `dispute` o `do_not_call`. Fue un error, y se vio
  // **al día siguiente**: el gestor se encontró una conversación que no era
  // ninguna de las dos y no tenía dónde ponerla. Se recupera el mismo código
  // —no uno nuevo— porque las 4 filas históricas son justo este caso:
  // conversaciones de 281 s de media, o sea discusiones largas, no cuelgues.
  'refuses',
  'dispute',
  'wrong_person',
  'voicemail',
  'do_not_call',
];

/**
 * Códigos retirados.
 *
 * ⚠️ **Siguen en `CALL_OUTCOMES` a propósito.** El modelo declara
 * `outcome: { type: String, enum: [...CALL_OUTCOMES, null] }`, así que sacarlos
 * del array dejaría **173 filas históricas sin poder guardarse**. Se retiran solo
 * de lo seleccionable: se leen, se pintan y se guardan como siempre.
 */
const CALL_OUTCOMES_DEPRECATED = ['payment_promise', 'payment_plan', 'call_back', 'wrong_data'];

const CALL_OUTCOMES = [...CALL_OUTCOMES_SELECTABLE, ...CALL_OUTCOMES_DEPRECATED];

/**
 * Spanish labels for call outcomes (UI)
 *
 * Incluye los retirados: el histórico se sigue pintando con su nombre, no con el
 * código en crudo.
 */
const CALL_OUTCOME_LABELS = {
  hangs_up_on_intro: 'Cuelga al presentarnos',
  interested: 'Interesado en pagar',
  // Antes «Se niega», que era ambiguo —¿se niega a pagar, o a hablar?—. El
  // nombre nuevo dice exactamente lo que pasó.
  refuses: 'Dice que no va a pagar',
  dispute: 'Abre disputa',
  wrong_person: 'No es la persona',
  voicemail: 'Salta el buzón',
  do_not_call: 'No llamar más',
  // Retirados el 22/10/2026.
  payment_promise: 'Promesa de pago',
  payment_plan: 'Plan de pagos',
  call_back: 'Volver a llamar',
  wrong_data: 'Datos incorrectos',
};

/**
 * Las tipificaciones que significan «el deudor quiere pagar».
 *
 * Viven en una lista porque **tres sitios distintos tienen que coincidir**: el
 * aviso «Promesa de pago el 14/10» de la ficha de la siguiente llamada
 * (`promesaPrevia`), el KPI `promises` del panel y el contador de la sesión en el
 * navegador. Si cada uno llevara su propia lista, al retirar un código uno se
 * quedaría atrás y el aviso desaparecería sin que nada fallara.
 */
const CALL_OUTCOMES_INTERESTED = ['interested', 'payment_promise', 'payment_plan'];

/**
 * Estados de un número del pool de marcación.
 *
 * `pending_verification` es el estado de un número externo (propio o de un
 * cliente) al que Telnyx ya ha mandado el código pero que todavía no se puede
 * usar como `from`.
 *
 * Un número NO se borra: se marca `disconnected`. El histórico de
 * `callattempts` y la serie de `callnumberdailies` apuntan a él.
 */
const CALL_PHONE_STATUSES = ['pending_verification', 'active', 'disconnected'];

const DEFAULT_CALL_PHONE_STATUS = 'pending_verification';

const CALL_PHONE_STATUS_LABELS = {
  pending_verification: 'Pendiente de verificar',
  active: 'Activo',
  disconnected: 'Desconectado',
};

/**
 * Cómo se obtuvo el número.
 *
 * - `telnyx`: comprado a Telnyx por API. Sirve para entrantes y salientes.
 * - `verified`: número de otro operador (nuestro o del cliente) verificado vía
 *   `/verified_numbers`. **Solo saliente**: las entrantes siguen llegando a su
 *   operador original.
 */
const CALL_PHONE_PROVIDERS = ['telnyx', 'verified'];

/**
 * Para qué se usa el número.
 *
 * ⚠️ **`dialer` y `whatsapp` son conjuntos DISJUNTOS, por diseño.**
 *
 * Los móviles contratados para el motor de WhatsApp son la identidad con la que
 * se activó cada sesión: se creó el número, se pidió a WhatsApp verificación por
 * llamada y se leyó el código. Si esos mismos números marcan en volumen entran en
 * el perfil que los operadores españoles bloquean, y el día que uno se suspenda no
 * se pierde un número de llamadas: se pierde la cuenta de WhatsApp que cuelga de
 * él — que es el canal de producción principal.
 *
 * Por eso la separación es una constante y una validación, no una convención.
 */
const CALL_PHONE_USAGES = ['dialer', 'whatsapp'];

/**
 * Tope de marcaciones diarias por número antes de arriesgar la reputación.
 *
 * El consenso del sector está en 25-75/día; por encima de 100 hay escrutinio y por
 * encima de 200 el marcado como spam es muy probable. 60 deja margen y cuadra con
 * ~7 números para las ~420 marcaciones diarias de un gestor.
 */
const DEFAULT_DAILY_DIAL_CAP = 60;

/**
 * Un número nuevo se calienta: 10-20 llamadas/día la primera semana. Subir de
 * golpe a la capacidad plena en un número recién comprado dispara el marcado como
 * spam, y entonces el número ya no se recupera.
 */
const WARMUP_DAILY_DIAL_CAP = 15;
const WARMUP_DAYS = 7;

/**
 * Ventana legal de llamadas.
 *
 * La LGDCU prohíbe llamar antes de las 9:00 y después de las 21:00, y en fines
 * de semana y festivos. No es una preferencia de producto: es el marco que
 * aplica a las empresas de recobro, y saltárselo es lo que acaba en una
 * reclamación ante la AEPD.
 *
 * Las horas son **hora de Madrid**, no la del servidor.
 */
const CALLING_WINDOW = { startHour: 9, endHour: 20, timeZone: 'Europe/Madrid' };

/**
 * Cuánto se espera antes de volver a intentar una llamada que no dio resultado.
 *
 * **3,7 h no es un número redondo por casualidad.** Es el espaciado que hace que
 * en la ventana de 9 a 20 —once horas— quepan exactamente **tres** intentos:
 *
 *     9:00 · 12:42 · 16:24      (el cuarto caeria a las 20:06, fuera)
 *
 * Decisión de Edgar del 23/09/2026: máximo tres llamadas al día a un mismo
 * deudor, y el tope se consigue por espaciado en vez de por un contador.
 *
 * ⚠️ **Eso ata el tope a la anchura de la ventana.** Si alguien mueve
 * `startHour` o `endHour`, el número de llamadas diarias cambia solo y sin que
 * nadie se entere. Hay un test que fija esa aritmética justamente para que, el
 * día que se toque el horario, salte y haya que decidir a conciencia.
 */
const RETRY_SPACING_HOURS = 3.7;

/**
 * Región para los festivos, en el formato de `date-holidays`.
 *
 * `('ES','MD')` es lo que ya usa `utils/assignmentUtils.js` para los días
 * hábiles, y se mantiene por coherencia. ⚠️ **El domicilio social está en
 * Barcelona desde 2024**, así que probablemente debería ser `('ES','CT')` — pero
 * cambiarlo aquí solo arreglaría la mitad: afectaría también a la programación
 * de assignments y a la compactación de WhatsApp. Se cambia en los tres sitios a
 * la vez o en ninguno.
 */
const HOLIDAY_REGION = ['ES', 'MD'];

/**
 * Líneas marcadas por gestor libre.
 *
 * **1 = progresivo**: nunca se marca más de lo que hay quien atienda, así que la
 * tasa de abandono es cero por construcción. Con un solo gestor es la única
 * opción defendible: con N=5 y una tasa de contacto del 20 %, el 26 % de las
 * tandas produce dos descolgadas o más y **un tercio de quien coge el teléfono
 * se encuentra con nadie al otro lado**.
 *
 * Subirlo exige ≥8 gestores simultáneos en el mismo pool y el tratamiento de
 * abandono montado. Y ojo, es contraintuitivo: **cuando sube la tasa de contacto
 * hay que BAJAR N**, porque el óptimo mantiene `N × p ≈ 1`.
 */
const DEFAULT_LINES_PER_AGENT = 1;

/**
 * Cuánto se adelanta el marcador a que un gestor se libere. Cubre el hueco entre
 * que cuelga y está listo, para que el siguiente ya esté sonando.
 */
const DEFAULT_LOOKAHEAD_SECONDS = 8;

module.exports = {
  CALLING_WINDOW,
  RETRY_SPACING_HOURS,
  HOLIDAY_REGION,
  DEFAULT_LINES_PER_AGENT,
  DEFAULT_LOOKAHEAD_SECONDS,
  AMD_VERDICTS,
  CALL_OUTCOMES,
  CALL_OUTCOMES_SELECTABLE,
  CALL_OUTCOMES_DEPRECATED,
  CALL_OUTCOMES_INTERESTED,
  CALL_OUTCOME_LABELS,
  CALL_PHONE_STATUSES,
  DEFAULT_CALL_PHONE_STATUS,
  CALL_PHONE_STATUS_LABELS,
  CALL_PHONE_PROVIDERS,
  CALL_PHONE_USAGES,
  DEFAULT_DAILY_DIAL_CAP,
  WARMUP_DAILY_DIAL_CAP,
  WARMUP_DAYS,
};
